import {api} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {AssignmentPanel} from "@/shared/admin/assignmentPanel";
import * as Constants from "@/shared/constants";
import {FormUtil} from "@/shared/forms/formUtil";
import {promiseNow} from "@/shared/utils/promise";
import {UserGroupAssignmentEntry, UserGroupAssignmentPanel} from "@/shared/admin/userGroupAssignmentPanel";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {debounce, toggleNavigationTabs} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {AssignedGroup, AssignedUser, Car, ErrorResponse} from "@/shared/timr";
import Modal = BootstrapModal.Modal;
import {toAssignableUser} from "@/shared/admin/toAssignableUserHelper";


export class CarsAdministrationPage {
	private table: CarsTable;
	private dialog: CarDialogHandler;

	constructor() {
		this.table = new CarsTable({
			element: $("#carsTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildTableFilterData()
		});

		this.dialog = new CarDialogHandler();

		const filterTable = () => this.table.filter(this.buildTableFilterData());
		$("#carsFilterUsable").change(filterTable);
		$("#carsFilterNameOrPlate").on("input", debounce(filterTable, this, 350));

		$("#carsAddButton").click(() => {
			if (SecurityStatus.isAddCarAllowed()) {
				this.dialog.create();
			} else {
				BootstrapModal.info({
					title: "limit.car.title",
					message: "limit.car"
				});
			}
		});

		$(this.table)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + CarsTable.EVENT_CAR_EDIT, (event, car: Car) => {
				this.dialog.edit(car);
			})
			.on(CarsTable.EVENT_CAR_DELETE, (event, car: Car) => {
				this.deleteCar(car);
			});

		$(this.dialog)
			.on(CarDialogHandler.EVENT_CAR_UPDATED, (event, car: Car) => {
				this.table.reload();
			})
			.on(CarDialogHandler.EVENT_CAR_DELETE, (event, car: Car) => {
				this.deleteCar(car);
				this.dialog.close();
			});
	}

	private deleteCar(car: Car) {
		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.delete.car.message", car.name),
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal, button) => {
				api("admin/checkIfDriveLogExistsForCar.json")
					.get({
						id: car.id
					})
					.then(data => {
						if (data.foundRecordings === 0) {
							this.performDeleteCar(car, modal);
						} else {
							this.showDeleteCarDialog(car, data.foundRecordings, modal);
						}
					});
			}
		});
	}

	private performDeleteCar(car: Car, modal ?: BootstrapModal.Modal) {
		api("admin/deleteCar.json")
			.post({
				id: car.id
			})
			.then(data => {
				SecurityStatus.update(data.securityStatus);
				this.table.reload();
				if (modal) {
					modal.close();
				}
			});
	}

	private showDeleteCarDialog(car: Car, foundRecordings: number, modal ?: BootstrapModal.Modal) {
		if (modal) {
			modal.close();
		}

		BootstrapModal.confirm({
			title: "dialog.warning.title",
			message: messages.withArguments("dialog.confirm.delete.car", foundRecordings),
			yesLabel: "confirmation.delete",
			yesAction: modal2 => this.performDeleteCar(car, modal2),
			noLabel: "confirmation.cancel"
		});
	}

	private buildTableFilterData() {
		return [
			{
				name: "nameOrPlate",
				value: $("#carsFilterNameOrPlate").val() as string
			},
			{
				name: "usable",
				value: $("#carsFilterUsable").val() as string
			}
		];
	}
}


class CarsTable extends BasicDataTable<Car> {
	public static EVENT_CAR_EDIT = "eventCarEdit";
	public static EVENT_CAR_DELETE = "eventCarDelete";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/cars.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			rowCallback: (row, data, index) => {
				const car = data as Car;
				if (!car.usable) {
					$(row).addClass("archived");
				}
			},
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<Car>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "plate", orderable: true, render: BasicDataTable.renderText},
			{
				data: "usable",
				orderable: false,
				render: this.renderUsable,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "car.context.editcar",
				action: car => { $(this).triggerHandler(CarsTable.EVENT_CAR_EDIT, car); }
			})
			.addMenuEntry({
				label: "car.context.disablecar",
				action: car => this.disableCar(car),
				show: car => car.usable
			})
			.addMenuEntry({
				label: "car.context.enablecar",
				action: car => this.enableCar(car),
				show: car => !car.usable
			})
			.addMenuEntry({
				label: "car.context.deletecar",
				action: car => { $(this).triggerHandler(CarsTable.EVENT_CAR_DELETE, car); }
			});
	}

	private disableCar(car: Car) {
		api("admin/disableCar.json")
			.post({
				id: car.id
			})
			.then(data => {
				SecurityStatus.update(data.securityStatus);
				this.reload();
			});
	}

	private enableCar(car: Car) {
		if (!SecurityStatus.isAddCarAllowed()) {
			BootstrapModal.warn({
				title: "limit.car.title",
				message: "limit.car"
			});
			return;
		}

		api("admin/enableCar.json")
			.post({
				id: car.id
			})
			.then(data => {
				SecurityStatus.update(data.securityStatus);
				this.reload();
			});
	}

	private renderUsable(this: void, data: boolean, type: string, recording: Car, meta: DataTables.CellMetaSettings): string {
		return data ? "" : `<i class="fal fa-archive" title="${messages["car.notusable"]}"></i>`;
	}
}

class CarDialogHandler {
	public static EVENT_CAR_UPDATED = "eventCarUpdated";
	public static EVENT_CAR_DELETE = "eventCarDelete";

	private modal: Modal;
	private car: Car;
	private assignmentPanel: AssignmentPanel<UserGroupAssignmentEntry>;

	constructor() {
		this.modal = new Modal({
			title: "car.header.create",
			message: $("#carDialog"),
			destroyOnClose: false,
			size: "modal-xl",
			buttons: [{
				text: "dialog.save",
				css: "saveButton btn-primary",
				action: this.sendSaveCarRequest
			}, {
				text: "dialog.cancel",
				css: "cancelButton btn-default",
			}, {
				text: "dialog.delete",
				css: "deleteButton btn-outline-danger",
				left: true,
				action: () => { $(this).triggerHandler(CarDialogHandler.EVENT_CAR_DELETE, this.car); }
			}],
			onOpen: () => {
				this.resizeAssignmentPanels();
				this.updateAllAssignedWarningStyle();
				$("#carName").focus();
			},
			onClose: () => {
				this.assignmentPanel.setEditMode(false);
				FormUtil.switchTabNavigationToFirst($("ul.dialog-nav-tabs"));
			}
		});

		// Resize assignment panels on window size change
		$(window).resize(debounce(() => this.resizeAssignmentPanels(), this, 50));

		// Users stay the same so only load promise once
		const userSource = api("admin/getAssignableUsers.json").get()
			.then(response => response.data.map(toAssignableUser));

		const assignedTo = () => {
			const carId = $("#carId").val();

			const promise = carId
				? api("admin/loadCarAssignments.json")
					.get({
						carId: carId
					})
					.then(data => [...data.groups, ...data.users])
				: promiseNow<(AssignedUser | AssignedGroup)[]>([]);

			return promise.then(data => {
				this.updateAllAssignedWarningStyle();
				return data;
			});
		};

		this.assignmentPanel = new UserGroupAssignmentPanel({
			panelElement: $("#userGroupAssignmentPanel"),
			assignedSourceProvider: assignedTo,
			selectSourceProvider: () => userSource,
			editMessage: SecurityStatus.isCorporateFeaturesAllowed() ? "admin.car.assign.usergroups" : "admin.car.assign.user",
			saveMessage: "admin.assignment.edit.end"
		});

		$('a[data-toggle="tab"]').on("shown.bs.tab", e => {
			const tabId = $(e.target).attr("href");
			if (tabId === "#tabCarAssignedTo") {
				this.assignmentPanel.updateView();
			}
		});

		this.assignmentPanel.onEditModeChanged(() => {
			const editMode = this.assignmentPanel.isInEditMode();

			this.modal.toggleButton("saveButton", !editMode);
			this.modal.toggleButton("cancelButton", !editMode);
			this.modal.toggleButton("deleteButton", !editMode && this.car.id !== null);
			toggleNavigationTabs("#tabNavigation", editMode);
		});

		const onAssignmentChange = () => {
			this.updateAllAssignedWarningStyle();
		};

		this.assignmentPanel.onAssignment(onAssignmentChange);
		this.assignmentPanel.onUnassignment(onAssignmentChange);

		$("#carName").change(() => this.updateTitle());
	}

	public open() {
		this.modal.show();
	}

	public close() {
		this.modal.close();
	}

	public create() {
		this.modal.toggleButton("deleteButton", false);
		this.resetCar();
		this.open();
	}

	public edit(car: Car) {
		this.modal.toggleButton("deleteButton", true);
		this.setCar(car);
		this.open();
	}

	public resetCar() {
		this.setCar({
			id: null,
			version: 0,
			name: "",
			plate: "",
			usable: true,
			externalId: ""
		});
	}

	public setCar(car: Car) {
		this.car = car;

		$("#carId").val(car.id);
		$("#carVersion").val(car.version);
		$("#carName").val(car.name);
		$("#carPlate").val(car.plate);
		$("#carUsable").prop("checked", car.usable);
		$("#carExternalId").val(car.externalId);

		$("#nameFilter").val("");

		this.assignmentPanel.reloadAssigned();

		FormUtil.removeAllFeedback($("#carForm"), true);
		this.updateTitle();
	}

	private updateTitle() {
		let message = messages[this.car.id == null ? "car.header.create" : "car.header.edit"];

		const carName = $("#carName").val() as string;
		if (carName) {
			message += ": " + carName;
		}

		this.modal.setTitle(message, true);
	}

	private sendSaveCarRequest = (modal: Modal, button: JQuery) => {
		Button.setLoading(button);

		const serializedCar = $("#carForm").serializeArray();

		const leaders = this.assignmentPanel.getAssigned()
			.map(assigned => ({
				name: assigned.elementType === "USER" ? "assignedToUsers" : "assignedToGroups",
				value: "" + assigned.id
			}));

		serializedCar.push(...leaders);

		api("admin/saveCar.json")
			.post(serializedCar)
			.done(data => {
				SecurityStatus.update(data.securityStatus);

				$(this).triggerHandler(CarDialogHandler.EVENT_CAR_UPDATED, data.car);
				this.modal.close();
			})
			.always(() => {
				Button.resetLoading(button);
			})
			.fail((data: ErrorResponse) => {
				if (data.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors($("#carForm"), data.responseJSON.fieldErrors, true);
				}
			});
	}


	private resizeAssignmentPanels() {
		const screenHeight = $(window).height();

		// Estimated height of other dialog components + padding + margin ...
		const panelHeight = Math.max(screenHeight - 730, 240);
		this.assignmentPanel.setScrollHeight(panelHeight);
	}

	private updateAllAssignedWarningStyle() {
		const assigned = this.assignmentPanel.getAssigned();
		$("#showAllCarsWarning")
			.toggleClass("alert-info", assigned.length === 0)
			.toggleClass("alert-warning", assigned.length !== 0);

	}
}
